// Library and levels sync through the user's Turso database via the Depth API (SYNC.md, DEPTH.md §8, TAGS.md §1).
// The statements are the reference ones (sql.ts); the client algorithm is SyncService.swift's / LevelsSync.swift's.
import { account } from "../api/account";
import { api, errorBody, problem, problemMessage } from "../api/client";
import { documentFor, documentsByKey } from "../levels/document";
import { levelsStore as levels } from "../levels/levelsStore";
import type { Structure } from "../levels/types";
import { library } from "../model/library";
import type { Article, Meta } from "../model/article";
import { taxonomy } from "../tags/taxonomyStore";
import { Hub, nowMs } from "../util";
import { body, I, int, num, parseResult, str, SyncError, type Row, type Value } from "./hrana";
import { applyRow, beatsSig, decodeNotes, encodeNotes, encodeStructure, type LevelsRow } from "./levelsLogic";
import * as SQL from "./sql";

export async function execute(sql: string, args: (Value | boolean)[] = [], signal?: AbortSignal): Promise<Row[]> {
  let res: Response;
  try {
    res = await api("/v1/sync/v2/pipeline", { method: "POST", json: body(sql, args), signal });
  } catch (e) {
    throw new SyncError("Couldn't reach the sync server.", "http");
  }
  if (!res.ok) {
    const { json } = await errorBody(res);
    const p = problem(res.status, json);
    if (p?.kind === "signedOut" || res.status === 401) throw new SyncError("Signed out. Sign in to sync.", "unauthorized");
    if (p) throw new SyncError(problemMessage(p), p.kind === "syncUnavailable" ? "unavailable" : "database");
    throw new SyncError(`Sync database error (HTTP ${res.status})`, "http");
  }
  return parseResult(await res.json());
}

interface State {
  dirty: Record<string, { meta: boolean; progress: boolean }>;
  tombstones: Record<string, number>;
  lastSeq: number;
  levelsPending: { key: string; chapter: number; structure: boolean }[];
  levelsSeq: number;
}

const STATE_KEY = "depth.state.sync";

function loadState(): State {
  try {
    return { dirty: {}, tombstones: {}, lastSeq: 0, levelsPending: [], levelsSeq: 0, ...JSON.parse(localStorage.getItem(STATE_KEY) || "{}") };
  } catch { return { dirty: {}, tombstones: {}, lastSeq: 0, levelsPending: [], levelsSeq: 0 }; }
}

/** Fields older builds' meta may lack (LISTENING.md, TAGS.md, IMAGES.md): kept when the remote meta doesn't have them. */
const OPTIONAL = ["skipped", "skipReasons", "fillerSource", "fillerModel", "quoteParagraphs", "jevQuotes", "tags", "images"];

class Sync {
  state = loadState();
  syncing = false;
  lastError: string | null = null;
  lastSyncAt: number | null = null;
  readonly events = new Hub<{ status: void; remoteProgress: string }>();
  /** True while this browser plays the article (remote progress then waits; SYNC.md "Applying a row"). */
  isActivelyPlaying: (id: string) => boolean = () => false;
  private pending = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private ticker: ReturnType<typeof setInterval> | undefined;
  private fetched = new Map<string, Promise<void>>();

  constructor() {
    library.onLocalChange = (id, meta, progress) => {
      const d = this.state.dirty[id] ?? { meta: false, progress: false };
      this.state.dirty[id] = { meta: d.meta || meta, progress: d.progress || progress };
      this.save();
      this.schedule();
    };
    library.onLocalDelete = (id) => {
      delete this.state.dirty[id];
      this.state.tombstones[id] = nowMs();
      this.save();
      this.schedule(0.5);
    };
    levels.onStructureGenerated = (key, chapter) => {
      this.state.levelsPending = this.state.levelsPending.filter((p) => !(p.key === key && p.chapter === chapter));
      this.state.levelsPending.push({ key, chapter, structure: true });
      this.save(); this.schedule();
    };
    levels.onNoteGenerated = (key, chapter) => {
      if (this.state.levelsPending.some((p) => p.key === key && p.chapter === chapter)) return;
      this.state.levelsPending.push({ key, chapter, structure: false });
      this.save(); this.schedule();
    };
    levels.onCleared = (key) => {
      this.state.levelsPending = this.state.levelsPending.filter((p) => p.key !== key);
      this.fetched.delete(key);
      this.save();
      if (this.enabled) execute(SQL.LEVELS_DELETE_SQL, [key]).catch(() => {});
    };
  }

  get enabled(): boolean { return account.signedIn && account.features.sync; }

  private save() { try { localStorage.setItem(STATE_KEY, JSON.stringify(this.state)); } catch { /* ignore */ } }

  /** A pass soon (coalescing bursts of local changes). */
  schedule(seconds = 3) {
    if (!this.enabled) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.syncNow(), seconds * 1000);
  }

  /** Every ~30 s while the page is visible; a pass when it's hidden (SYNC.md "Sync pass"). */
  start() {
    const tick = () => { if (document.visibilityState === "visible") this.syncNow(); };
    clearInterval(this.ticker);
    this.ticker = setInterval(tick, 30_000);
    document.addEventListener("visibilitychange", () => this.syncNow());
    window.addEventListener("online", () => this.syncNow());
    this.syncNow();
  }

  async syncNow(): Promise<void> {
    if (!this.enabled) return;
    if (this.syncing) { this.pending = true; return; }
    this.syncing = true;
    this.events.emit("status", undefined);
    try {
      do {
        this.pending = false;
        await this.pushTombstones();
        await this.pushDirty();
        await this.pull();
        await this.syncTaxonomy();
        await this.levelsPass();
        this.lastError = null;
        this.lastSyncAt = Date.now();
      } while (this.pending);
    } catch (e) {
      this.lastError = e instanceof Error ? e.message : String(e);
    } finally {
      this.syncing = false;
      this.events.emit("status", undefined);
    }
  }

  // ---- articles

  private async pushTombstones() {
    for (const [id, at] of Object.entries(this.state.tombstones)) {
      const rows = await execute(SQL.DELETE_SQL, [id, I(at)]);
      delete this.state.tombstones[id];
      this.save();
      if (rows[0]) this.apply(rows[0]);
    }
  }

  private async pushDirty() {
    for (const [id, flags] of Object.entries(this.state.dirty)) {
      const a = library.get(id);
      if (!a || a.isBook) { delete this.state.dirty[id]; continue; }
      if (a.status !== "ready") continue;
      const sendMeta = flags.meta || !a.synced, sendProgress = flags.progress || !a.synced;
      const rows = await execute(SQL.PUSH_SQL, [id, sendMeta ? JSON.stringify(a.meta) : null, I(sendMeta ? (a.metaUpdatedAt || nowMs()) : 0),
        I(a.progress.chunk), a.progress.offset, a.progress.finished, I(sendProgress ? a.progressUpdatedAt : 0)]);
      const cur = library.get(id);
      if (cur && cur.metaUpdatedAt === a.metaUpdatedAt && cur.progressUpdatedAt === a.progressUpdatedAt) delete this.state.dirty[id];
      this.save();
      if (rows[0]) this.apply(rows[0]);
    }
    for (const a of library.list()) {
      if (a.status === "ready" && !a.isBook && !a.synced && !this.state.dirty[a.id]) {
        this.state.dirty[a.id] = { meta: true, progress: true };
        this.pending = true;
      }
    }
    this.save();
  }

  private async pull() {
    const max = int((await execute(SQL.MAX_SQL))[0]?.seq);
    if (max < this.state.lastSeq) {
      // The database was wiped or replaced: push everything again and pull from the start.
      this.state.lastSeq = 0;
      for (const a of library.list()) if (a.status === "ready") { library.store({ ...a, synced: false }); this.state.dirty[a.id] = { meta: true, progress: true }; }
      this.save();
      this.pending = true;
      return;
    }
    let since = this.state.lastSeq;
    for (;;) {
      const rows = await execute(SQL.CHANGES_SQL, [I(since)]);
      for (const r of rows) this.apply(r);
      if (rows.length) since = int(rows[rows.length - 1].seq);
      if (rows.length < 200) break;
    }
    this.state.lastSeq = since;
    this.save();
  }

  /** One database row → the local library (never bumps local timestamps). */
  apply(row: Row) {
    const id = str(row.id);
    if (!id) return;
    if (int(row.deleted)) {
      delete this.state.dirty[id];
      delete this.state.tombstones[id];
      if (library.get(id)) library.removeLocal(id);
      return;
    }
    const metaText = str(row.meta);
    if (!metaText) return;
    let meta: Meta;
    try { meta = JSON.parse(metaText); } catch { return; }
    if (!meta || typeof meta !== "object") return;
    const metaAt = int(row.meta_updated_at), progressAt = int(row.progress_updated_at);
    let a = library.get(id);
    let isNew = false;
    if (!a) {
      if (this.state.tombstones[id]) return;
      // The same link added on two devices before either synced: keep the database's copy.
      const twin = meta.sourceURL ? library.list().find((x) => x.meta.sourceURL === meta.sourceURL && !x.synced && x.status === "ready") : undefined;
      if (twin) { delete this.state.dirty[twin.id]; library.removeLocal(twin.id); }
      a = { id, status: "ready", meta: { title: meta.title ?? "Article" }, metaUpdatedAt: 0, progress: { chunk: 0, offset: 0, finished: false },
        progressUpdatedAt: 0, synced: true };
      isNew = true;
    }
    let next: Article = { ...a };
    if (metaAt > a.metaUpdatedAt) {
      const merged: Meta = { ...meta };
      for (const k of OPTIONAL) if (!(k in meta) && k in a.meta) merged[k] = a.meta[k];
      next = { ...next, meta: merged, metaUpdatedAt: metaAt, status: "ready", error: undefined };
    }
    let progressApplied = false;
    if (progressAt > a.progressUpdatedAt && !this.isActivelyPlaying(id)) {
      next.progress = { chunk: int(row.progress_chunk), offset: num(row.progress_offset), finished: !!int(row.progress_finished) };
      next.progressUpdatedAt = progressAt;
      if (this.state.dirty[id]) this.state.dirty[id].progress = false;
      progressApplied = true;
    }
    const n = next.meta.chunks?.length ?? 0;
    if (n && next.progress.chunk >= n) next.progress = { chunk: 0, offset: 0, finished: next.progress.finished };
    next.synced = true;
    library.store(next);
    if (progressApplied && !isNew) this.events.emit("remoteProgress", id);
  }

  // ---- the tag list

  private async syncTaxonomy() {
    const row = (await execute(SQL.TAXONOMY_PULL_SQL))[0];
    if (row && typeof row.json === "string") taxonomy.applyRemote(row.json, int(row.updated_at));
  }

  // ---- generated levels (DEPTH.md §8)

  /** Fetches an item's rows once per session (≤3 s) so nothing already generated elsewhere is generated again. */
  levelsReady(key: string): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    let p = this.fetched.get(key);
    if (!p) {
      p = (async () => {
        await levels.load(key);
        try {
          const rows = await execute(SQL.LEVELS_ITEM_SQL, [key], AbortSignal.timeout(3000));
          for (const r of rows) this.applyLevels(r);
        } catch { /* generate anyway */ }
      })();
      this.fetched.set(key, p);
    }
    return p;
  }

  private paragraphCount(key: string, chapter: number): number | null {
    const a = library.get(key);
    const doc = documentsByKey.get(key) ?? (a && a.status === "ready" ? documentFor(a) : null);
    return doc?.chapters[chapter] ? doc.chapters[chapter].paragraphs.length : null;
  }

  private async levelsPass() {
    for (const p of [...this.state.levelsPending].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : a.chapter - b.chapter))) {
      await this.pushLevels(p);
    }
    const max = int((await execute(SQL.LEVELS_MAX_SQL))[0]?.seq);
    if (max < this.state.levelsSeq) this.state.levelsSeq = 0;
    let since = this.state.levelsSeq;
    for (;;) {
      const rows = await execute(SQL.LEVELS_CHANGES_SQL, [I(since)]);
      for (const r of rows) this.applyLevels(r);
      if (rows.length) since = int(rows[rows.length - 1].seq);
      if (rows.length < 200) break;
    }
    this.state.levelsSeq = since;
    this.save();
  }

  private async pushLevels(p: { key: string; chapter: number; structure: boolean }): Promise<void> {
    const drop = () => { this.state.levelsPending = this.state.levelsPending.filter((x) => !(x.key === p.key && x.chapter === p.chapter && x.structure === p.structure)); this.save(); };
    await levels.load(p.key);
    const s = levels.structure(p.key, p.chapter), paragraphs = this.paragraphCount(p.key, p.chapter);
    if (!s || paragraphs === null) {
      // Not open in this session yet: keep it queued if the article exists (its document is built when opened).
      if (!s || !library.get(p.key)) drop();
      return;
    }
    const sig = beatsSig(s), notes = encodeNotes(levels.notes(p.key, p.chapter));
    const stored = p.structure
      ? await execute(SQL.LEVELS_CLAIM_SQL, [p.key, I(p.chapter), I(paragraphs), sig, encodeStructure(s), notes, I(nowMs())])
      : await execute(SQL.LEVELS_NOTES_SQL, [p.key, I(p.chapter), sig, notes, I(nowMs())]);
    drop();
    if (!stored.length) {
      const row = (await execute(SQL.LEVELS_GET_SQL, [p.key, I(p.chapter)]))[0];
      if (row) this.applyLevels(row);
      else if (!p.structure) await this.pushLevels({ ...p, structure: true });
    }
  }

  private applyLevels(r: Row) {
    const key = str(r.item_key), structure = str(r.structure), sig = str(r.beats_sig);
    if (!key || !structure || sig === null) return;
    const chapter = int(r.chapter), local = this.paragraphCount(key, chapter);
    if (local === null || !levels.isLoaded(key)) return; // not in this library / not opened yet: fetched when opened
    const row: LevelsRow = { item_key: key, chapter, seq: int(r.seq), paragraphs: int(r.paragraphs), beats_sig: sig, structure, notes: str(r.notes) ?? "{}" };
    const out = applyRow(levels.structure(key, chapter), levels.notes(key, chapter), row, local);
    if (out.action !== "take" || !out.structure) return;
    if (out.changed) levels.applySynced(out.structure as Structure, out.notes, key, chapter);
    if (out.push_notes) {
      this.state.levelsPending.push({ key, chapter, structure: false });
      this.save(); this.schedule();
    }
  }
}

export const sync = new Sync();
export { decodeNotes };
